/**
 * features/comments/CommentReview.tsx
 * ------------------------------------------------------------------
 * AUDIT P1 — the EDITOR unified review-&-correct panel.
 *
 * One window, every comment for a student editable together:
 *   - each SUBJECT comment   (teacher original in reportMarks;
 *     editor correction saved as a per-subject OVERRIDE in
 *     reportComments — the #3 channel the report already prefers,
 *     so the teacher's original is never mutated)
 *   - the CLASS TEACHER comment   (reportComments)
 *   - the HEAD OF YEAR comment    (reportComments)
 *
 * Editor helpers:
 *   - "Tidy" : a built-in cleanup pass (double spaces, sentence
 *     capitalisation, spacing around punctuation, terminal stop).
 *     Honest scope: cleanup, NOT a grammar/spell checker. A true
 *     checker is a deferred provider decision (like AI/SMTP).
 *   - "Fix a word everywhere" : replace a term (e.g. a misspelt
 *     name) across ALL of this student's comments at once, with an
 *     optional whole-class scope behind a confirm.
 *
 * Editor / admin / headteacher only. Reads every class.
 * ------------------------------------------------------------------
 */
import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  doc,
  getDoc,
  // Round 6: needed for notification cleanup + flag updates.
  updateDoc,
  collection,
  query,
  where,
  getDocs,
  // Round 7: edit-history tab — newest-first audit query
  orderBy,
  limit,
} from 'firebase/firestore';
// Comments foundation swap — route setDoc through the undefined-safe wrapper
import { setDoc } from '@/lib/firebase';
import {
  Save as SaveIcon,
  Check,
  Wand2,
  Replace,
  ChevronLeft,
  ChevronRight,
  AlertTriangle,
  ClipboardCheck,
  // Round 6: flag banner icons
  Flag,
  X as XIcon,
  // Round 7: history tab + entry icons
  History,
  PenSquare,
  FileText,
} from 'lucide-react';
import { db } from '@/lib/firebase';
import { useData, COLLECTIONS } from '@/contexts/DataContext';
import { isClientStale } from '@/lib/version';
import { useDirtyFlag } from '@/contexts/DirtyContext';
import { useAuth } from '@/contexts/AuthContext';
import { isTermEditable, getLinkedTeacher } from '@/lib/permissions';
import { assembleClassReport } from '@/lib/assembleReport';
import { sanitizeComment, stripUndefined } from '@/lib/sanitize';
import { auditFromContext } from '@/lib/audit';
import {
  gradeColor,
  deriveMark,
  schemeForSubject,
  isAbsent,
} from '@/lib/grading';
import { loadLogoDataUrl } from '@/lib/loadLogo';
import {
  buildCommentProofPdf,
  type CommentProofStudent,
} from '@/lib/commentProofPdf';
import { TermKey, termLabel } from '@/lib/school.config';
import type {
  TermState,
  ReportCommentsDocument,
  StudentReportComments,
  CommentMeta,
  CAMonths,
  MaybeMark,
  // Round 6
  CommentFlag,
  FlagTarget,
} from '@/types';
import type { ReportStudent } from '@/lib/reportPdf';
import RaiseFlagModal from './RaiseFlagModal';
import { Spinner } from '@/components/ui/primitives';
import { SaveConfirmation, useAutoSave, StickySaveBar } from '@/components/ui/SaveConfirmation';
import {
  GradientHero,
  GlassPill,
  SoftCard,
  glassFieldClass,
} from '@/components/ui/glass';

const rgb = (c: [number, number, number]) =>
  `rgb(${c[0]},${c[1]},${c[2]})`;

/** Built-in tidy pass — cleanup only, NOT grammar checking. */
export function tidyText(s: string): string {
  let t = s.replace(/\s+/g, ' ').trim();
  t = t.replace(/\s+([.,;:!?])/g, '$1');
  t = t.replace(/([.,;:!?])(?=[^\s])/g, '$1 ');
  t = t.replace(
    /(^\s*|[.!?]\s+)([a-z])/g,
    (_m, p, c) => p + c.toUpperCase(),
  );
  if (t && !/[.!?]$/.test(t)) t += '.';
  return t;
}

interface SubjRow {
  subjectId: string;
  name: string;
  teacherName: string;
  original: string;
}

/* ==================================================================
   Round 7 — Edit History tab
   ==================================================================
   Read-only view of every comment edit captured by the audit log for
   the current (year, term, classId). Renders flattened-by-edit (one
   row per before/after change) and grouped by student.

   No new audit writes — the CommentReview save path (action
   'comments_edited_by_editor') and the ReviewWorkspace save path
   (action 'comments_saved') already record full before/after diffs
   per changed student.field. We just read what's already there.
   ================================================================== */

/** One flattened edit — the unit of display in the tab. */
interface EditRow {
  /** Audit doc id (stable React key + back-trace) */
  auditId: string;
  /** The audit doc's createdAt — newer rows shown first */
  createdAt: string;
  actorName: string;
  actorRole: string;
  studentId: string;
  studentName: string;
  /** 'classTeacherComment' | 'hodComment' | 'subjectComment:<subjId>' */
  field: string;
  before: string;
  after: string;
}

/** The shape we expect to read out of `details.edits[i]`. Defensive
 *  about missing fields — old/test audit rows may lack some. */
interface RawEdit {
  studentId?: string;
  studentName?: string;
  field?: string;
  before?: string;
  after?: string;
}

/** Pretty label for a stored `field` value (handles subject ids). */
function fieldLabel(field: string, subjName: (id: string) => string): string {
  if (field === 'classTeacherComment') return 'Class teacher comment';
  if (field === 'hodComment') return 'Head of Year comment';
  if (field.startsWith('subjectComment:')) {
    const sid = field.slice('subjectComment:'.length);
    return `${subjName(sid)} — subject comment`;
  }
  return field;
}

/** Short human time stamp — uses the locale that's likely most
 *  familiar to BW users (en-GB style for date, 24h time). */
function shortTime(iso: string): string {
  try {
    const d = new Date(iso);
    return d.toLocaleString('en-GB', {
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

interface EditHistoryProps {
  year: string;
  term: TermKey;
  classId: string;
  className: string;
  subjName: (id: string) => string;
  /** Roster of students in the class — id -> fullName fallback if the
   *  audit's stored name is missing/stale. */
  roster: { id: string; fullName: string }[];
  /** Click handler — jump to a specific student in the Review tab. */
  onOpenStudent: (studentId: string) => void;
}

function EditHistory({
  year,
  term,
  classId,
  className,
  subjName,
  roster,
  onOpenStudent,
}: EditHistoryProps) {
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState<EditRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [expandedRow, setExpandedRow] = useState<string | null>(null);

  // Audit fetch. We avoid composite indexes by only constraining
  // the indexed `createdAt` field server-side, then filter the rest
  // client-side. limit(500) keeps this bounded for a single class —
  // a busy term with 30 students has well under 500 edit events.
  // If you ever see this run dry-empty on a class where edits exist,
  // bump the limit and consider a composite index on category+action+createdAt.
  const load = useCallback(async () => {
    if (!classId) {
      setRows([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const snap = await getDocs(
        query(
          collection(db, COLLECTIONS.auditLog),
          orderBy('createdAt', 'desc'),
          limit(500),
        ),
      );
      const out: EditRow[] = [];
      snap.docs.forEach((d) => {
        const data = d.data() as {
          createdAt?: string;
          actorName?: string;
          actorRole?: string;
          action?: string;
          category?: string;
          details?: {
            year?: string;
            term?: string;
            classId?: string;
            edits?: RawEdit[];
          };
        };
        // Match only the comment-edit actions
        if (data.category !== 'academic') return;
        if (
          data.action !== 'comments_edited_by_editor' &&
          data.action !== 'comments_saved'
        ) {
          return;
        }
        const det = data.details ?? {};
        // Same (year, term, class) only
        if (det.year !== year) return;
        if (det.term !== term) return;
        if (det.classId !== classId) return;
        const edits = Array.isArray(det.edits) ? det.edits : [];
        const rosterName = (sid: string) =>
          roster.find((s) => s.id === sid)?.fullName;
        edits.forEach((e, i) => {
          if (!e.studentId || !e.field) return;
          // Skip rows where before === after (defensive — the writers
          // shouldn't emit these, but old data might).
          if ((e.before ?? '') === (e.after ?? '')) return;
          out.push({
            auditId: `${d.id}:${i}`,
            createdAt: data.createdAt ?? '',
            actorName: data.actorName ?? 'Unknown',
            actorRole: data.actorRole ?? '',
            studentId: e.studentId,
            studentName: e.studentName ?? rosterName(e.studentId) ?? '—',
            field: e.field,
            before: e.before ?? '',
            after: e.after ?? '',
          });
        });
      });
      setRows(out);
    } catch (e) {
      setError((e as Error).message);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [year, term, classId, roster]);

  useEffect(() => {
    load();
  }, [load]);

  // Group rows by student for the visual hierarchy, but keep newest
  // edits at the top within each group. Students themselves sorted
  // by most-recent-edit so the "most active" student is on top.
  const groups = useMemo(() => {
    const byStudent = new Map<
      string,
      { studentId: string; studentName: string; edits: EditRow[] }
    >();
    rows.forEach((r) => {
      const g = byStudent.get(r.studentId);
      if (g) g.edits.push(r);
      else
        byStudent.set(r.studentId, {
          studentId: r.studentId,
          studentName: r.studentName,
          edits: [r],
        });
    });
    const list = Array.from(byStudent.values());
    // Sort groups by their newest edit timestamp desc
    list.sort((a, b) => {
      const ta = a.edits[0]?.createdAt ?? '';
      const tb = b.edits[0]?.createdAt ?? '';
      return tb.localeCompare(ta);
    });
    return list;
  }, [rows]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner label="Loading edit history…" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-xl border border-red-200 bg-red-50 text-red-700 px-4 py-3 text-sm dark:bg-red-900/30 dark:border-red-700 dark:text-red-300">
        <AlertTriangle size={14} className="inline mr-1" />
        Couldn't load edit history: {error}
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <SoftCard pad="p-8">
        <div className="text-center text-sm text-gray-500 dark:text-gray-400">
          <History
            size={32}
            className="mx-auto mb-2 text-gray-300 dark:text-gray-600"
          />
          No comment edits recorded yet for{' '}
          <span className="font-medium">{className}</span> this term.
        </div>
      </SoftCard>
    );
  }

  return (
    <div className="space-y-4">
      <SoftCard pad="p-3">
        <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
          <span>
            <span className="font-semibold text-gray-700 dark:text-gray-200">
              {rows.length}
            </span>{' '}
            edit{rows.length === 1 ? '' : 's'} across{' '}
            <span className="font-semibold text-gray-700 dark:text-gray-200">
              {groups.length}
            </span>{' '}
            student{groups.length === 1 ? '' : 's'} —{' '}
            {className} · {termLabel(term)} {year}
          </span>
        </div>
      </SoftCard>

      {groups.map((g) => (
        <SoftCard key={g.studentId} pad="p-0">
          <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between">
            <div>
              <h3 className="font-semibold text-sm text-gray-800 dark:text-white">
                {g.studentName}
              </h3>
              <p className="text-[11px] text-gray-400 mt-0.5">
                {g.edits.length} edit
                {g.edits.length === 1 ? '' : 's'} · most recent{' '}
                {shortTime(g.edits[0].createdAt)}
              </p>
            </div>
            <button
              onClick={() => onOpenStudent(g.studentId)}
              className="text-xs font-semibold text-primary hover:underline dark:text-primaryLight"
            >
              Open in Review →
            </button>
          </div>
          <div className="divide-y divide-gray-100 dark:divide-gray-800">
            {g.edits.map((r) => {
              const open = expandedRow === r.auditId;
              return (
                <div key={r.auditId} className="px-4 py-3">
                  <button
                    type="button"
                    onClick={() =>
                      setExpandedRow(open ? null : r.auditId)
                    }
                    className="w-full flex items-start justify-between gap-3 text-left"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <PenSquare
                          size={12}
                          className="text-gray-400 shrink-0"
                        />
                        <span className="text-xs font-semibold text-gray-700 dark:text-gray-200">
                          {fieldLabel(r.field, subjName)}
                        </span>
                        <span className="text-[11px] text-gray-400">
                          edited by {r.actorName}
                          {r.actorRole ? ` (${r.actorRole})` : ''}
                        </span>
                        <span className="text-[11px] text-gray-400">
                          · {shortTime(r.createdAt)}
                        </span>
                      </div>
                      {!open && (
                        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400 line-clamp-1">
                          {r.after || (
                            <span className="italic">(cleared)</span>
                          )}
                        </p>
                      )}
                    </div>
                    <ChevronRight
                      size={14}
                      className={`text-gray-400 shrink-0 transition-transform ${
                        open ? 'rotate-90' : ''
                      }`}
                    />
                  </button>
                  {open && (
                    <div className="mt-3 space-y-2">
                      <div>
                        <p className="text-[10px] uppercase tracking-wide font-bold text-gray-500 mb-1">
                          Before
                        </p>
                        <div className="rounded-lg bg-red-50/70 dark:bg-red-900/15 border border-red-100 dark:border-red-900/40 px-3 py-2 text-xs text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
                          {r.before || (
                            <span className="italic text-gray-400">
                              (empty)
                            </span>
                          )}
                        </div>
                      </div>
                      <div>
                        <p className="text-[10px] uppercase tracking-wide font-bold text-gray-500 mb-1">
                          After
                        </p>
                        <div className="rounded-lg bg-emerald-50/70 dark:bg-emerald-900/15 border border-emerald-100 dark:border-emerald-900/40 px-3 py-2 text-xs text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
                          {r.after || (
                            <span className="italic text-gray-400">
                              (cleared)
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </SoftCard>
      ))}
    </div>
  );
}

export default function CommentReview({
  year,
  term,
  // Round 6: optional deep-link from the Comment Flags screen.
  // When set, we fetch the flag, auto-select its class + student,
  // highlight the target field, and show a banner with in-context
  // Resolve / Dismiss actions.
  flagId,
}: {
  year: string;
  term: TermKey;
  flagId?: string;
}) {
  const { classes, students: allStudents, subjects, teachers, users, termStatuses, serverBuild } =
    useData();
  // Exclude 'pending' (Pastel-only, not yet enrolled) from comment rosters.
  const students = allStudents.filter((s) => s.status !== 'pending');
  const { userData, userRole, permissions } = useAuth();

  const isEditorRole =
    userRole === 'editor' ||
    userRole === 'admin' ||
    userRole === 'headteacher';

  const termState: TermState =
    termStatuses.find((t) => t.year === year && t.term === term)
      ?.status ?? 'open';
  const editable =
    isTermEditable(termState, userRole) && isEditorRole;

  const [classId, setClassId] = useState('');
  const [rows, setRows] = useState<ReportStudent[]>([]);
  const [studentIds, setStudentIds] = useState<string[]>([]);
  const [idx, setIdx] = useState(0);
  const [pendingStudent, setPendingStudent] = useState<
    string | null
  >(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(
    null,
  );
  // Round 12: full-screen save modal replaces the inline toast.
  const [saveModal, setSaveModal] = useState<{
    show: boolean;
    status: 'saving' | 'success' | 'error';
    message: string;
  }>({ show: false, status: 'saving', message: '' });
  // Version gate (slice 1): a stale client is blocked from saving entirely.
  const [staleBlocked, setStaleBlocked] = useState(false);
  // Version gate (slice 2): surface the existing `dirty` flag into the global
  // registry so blanket auto-reload never fires while comment edits are unsaved.
  // CommentReview is banner-only for restore (no draft-stash) - see report.
  useDirtyFlag('commentreview', dirty);

  const [subjRows, setSubjRows] = useState<Record<string, SubjRow[]>>(
    {},
  );
  // Raw mark entries per student→subject (CA months + exam + comment author),
  // captured for the editor's proof PDF (13.6). Keyed studentId → subjectId.
  const [markData, setMarkData] = useState<
    Record<string, Record<string, { ca?: CAMonths; exam?: MaybeMark; comment?: string; teacherId?: string }>>
  >({});
  const [edits, setEdits] = useState<
    Record<
      string,
      {
        subjects: Record<string, string>;
        classTeacher: string;
        hoy: string;
      }
    >
  >({});
  const [existing, setExisting] = useState<
    Record<string, StudentReportComments>
  >({});

  const [findText, setFindText] = useState('');
  const [replText, setReplText] = useState('');
  const [classScope, setClassScope] = useState(false);
  const [confirmBulk, setConfirmBulk] = useState<null | {
    count: number;
  }>(null);

  // Round 6: deep-link state. `flagContext` is the loaded flag doc
  // when arriving via #/commentReview/<flagId>; null otherwise.
  // `highlightedAt` is a timestamp used to flash a ring around the
  // target textarea once the student loads. We also track
  // `flagResolveBusy` for the banner's Resolve / Dismiss actions.
  const [flagContext, setFlagContext] = useState<CommentFlag | null>(
    null,
  );
  const [highlightedAt, setHighlightedAt] = useState<number | null>(
    null,
  );
  const [flagResolveBusy, setFlagResolveBusy] = useState(false);
  const [flagDismissReason, setFlagDismissReason] = useState('');
  // Inline "flag this comment" modal (editors) — notifies the teacher
  // (bell + push + email) via raiseFlag() / the onCommentFlagged function.
  const [flagModal, setFlagModal] = useState<
    | null
    | { target: FlagTarget; subjectId?: string; subjectName?: string; teacherName?: string; commentPreview?: string }
  >(null);
  const [showFlagDismiss, setShowFlagDismiss] = useState(false);

  // Round 7: top-level tab toggle between the editor surface and the
  // read-only edit-history view. We keep all the editor state alive
  // when on the history tab so switching back is instant — the
  // history view fetches its own data lazily.
  const [activeTab, setActiveTab] = useState<'review' | 'history'>(
    'review',
  );

  // Round 6: ref the target textarea wrapper so we can scrollIntoView
  // once the student loads. We re-key it by (subjectId, kind) so the
  // ring re-mounts when the editor navigates to a different flag.
  const highlightTargetRef = useRef<HTMLDivElement | null>(null);

  const subjName = useCallback(
    (id: string) =>
      subjects.find((s) => s.id === id)?.name ?? id,
    [subjects],
  );

  const roster = useMemo(
    () =>
      students
        .filter((s) => s.currentClass === classId)
        .sort((a, b) => a.fullName.localeCompare(b.fullName)),
    [students, classId],
  );

  // Round 6: load the flag doc when arriving via deep-link.
  // Auto-select its class. Once the class's roster loads, the second
  // effect (below) will set the student index and trigger the
  // highlight + scroll-into-view.
  useEffect(() => {
    if (!flagId) {
      setFlagContext(null);
      return;
    }
    let cancel = false;
    (async () => {
      try {
        const snap = await getDoc(doc(db, COLLECTIONS.flags, flagId));
        if (cancel) return;
        if (!snap.exists()) {
          setFlagContext(null);
          setMsg({ ok: false, text: 'Flag not found.' });
          return;
        }
        const flag = { id: snap.id, ...(snap.data() as object) } as CommentFlag;
        setFlagContext(flag);
        // Auto-select the class. If the user was already on the right
        // class, the roster effect will re-run on the next render
        // anyway thanks to the idx-setting effect.
        if (flag.classId && classId !== flag.classId) {
          setClassId(flag.classId);
        }
      } catch (e) {
        if (!cancel) setMsg({ ok: false, text: (e as Error).message });
      }
    })();
    return () => {
      cancel = true;
    };
  }, [flagId]); // intentionally not depending on classId — only run on flag change

  // Round 6: once the roster loads, locate the flagged student.
  useEffect(() => {
    if (!flagContext || !flagContext.studentId) return;
    if (roster.length === 0) return; // wait for roster
    const i = roster.findIndex((s) => s.id === flagContext.studentId);
    if (i >= 0 && i !== idx) setIdx(i);
    // Trigger the highlight ring (it auto-fades via CSS animate-pulse,
    // but the timestamp resets the React key when navigating between
    // flags so re-mounting plays the animation again).
    setHighlightedAt(Date.now());
  }, [flagContext, roster]); // eslint-disable-line react-hooks/exhaustive-deps

  // Round 6: after highlight is set + the data loads, scroll the
  // target textarea into view. We schedule on the next animation
  // frame to make sure the DOM has the target rendered.
  useEffect(() => {
    if (!highlightedAt) return;
    const t = requestAnimationFrame(() => {
      highlightTargetRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    });
    return () => cancelAnimationFrame(t);
  }, [highlightedAt]);

  // Round 6: clear notifications keyed to this flag. Best-effort.
  const clearFlagNotifications = useCallback(
    async (fid: string) => {
      try {
        const snap = await getDocs(
          query(
            collection(db, COLLECTIONS.notifications),
            where('flagId', '==', fid),
          ),
        );
        await Promise.all(
          snap.docs.map((d) =>
            updateDoc(doc(db, COLLECTIONS.notifications, d.id), {
              read: true,
              readAt: new Date().toISOString(),
              clearedByFlagResolution: true,
            }),
          ),
        );
      } catch (e) {
        console.warn(
          '[CommentReview] notification cleanup failed:',
          (e as Error).message,
        );
      }
    },
    [],
  );

  // Round 6: in-context Resolve. Mirrors the auditing pattern from
  // CommentFlags.setStatus. Updates the flag, audits the change,
  // clears notifications, then unsets the local flag context so the
  // banner disappears.
  async function resolveFlagInContext() {
    if (!flagContext) return;
    setFlagResolveBusy(true);
    try {
      const fromStatus = flagContext.status;
      await updateDoc(doc(db, COLLECTIONS.flags, flagContext.id), {
        status: 'resolved',
        resolvedAt: new Date().toISOString(),
        resolvedById: userData?.uid ?? '',
        resolvedInContext: true,
      });
      auditFromContext(userData, 'flag_status_changed', 'academic', {
        targetId: flagContext.id,
        targetType: 'comment',
        targetName: `Flag on ${flagContext.studentName}`,
        details: {
          flagId: flagContext.id,
          fromStatus,
          toStatus: 'resolved',
          resolvedInContext: true,
          target: flagContext.target,
          year: flagContext.year,
          term: flagContext.term,
          classId: flagContext.classId,
          className: flagContext.className,
          studentId: flagContext.studentId,
          studentName: flagContext.studentName,
          subjectId: flagContext.subjectId,
          subjectName: flagContext.subjectName,
          reason: flagContext.reason,
        },
      });
      await clearFlagNotifications(flagContext.id);
      setFlagContext(null);
      setHighlightedAt(null);
      setMsg({ ok: true, text: 'Flag resolved.' });
      setTimeout(() => setMsg(null), 3500);
      // Strip the flagId from the URL so refreshing doesn't try to
      // re-load the now-resolved flag.
      if (window.location.hash.startsWith('#/commentReview/')) {
        window.history.replaceState(null, '', '#/commentReview');
      }
    } catch (e) {
      setMsg({ ok: false, text: (e as Error).message });
    } finally {
      setFlagResolveBusy(false);
    }
  }

  // Round 6: in-context Dismiss. Same effect as CommentFlags.dismissFlag
  // but with a reason captured here.
  async function dismissFlagInContext() {
    if (!flagContext) return;
    if (!flagDismissReason.trim()) return;
    setFlagResolveBusy(true);
    try {
      const fromStatus = flagContext.status;
      await updateDoc(doc(db, COLLECTIONS.flags, flagContext.id), {
        status: 'resolved',
        resolvedAt: new Date().toISOString(),
        resolvedById: userData?.uid ?? '',
        dismissedWithoutReview: true,
        dismissReason: flagDismissReason,
      });
      auditFromContext(userData, 'flag_dismissed', 'academic', {
        targetId: flagContext.id,
        targetType: 'comment',
        targetName: `Flag on ${flagContext.studentName}`,
        details: {
          flagId: flagContext.id,
          fromStatus,
          toStatus: 'resolved',
          dismissReason: flagDismissReason,
          target: flagContext.target,
          year: flagContext.year,
          term: flagContext.term,
        },
      });
      await clearFlagNotifications(flagContext.id);
      setFlagContext(null);
      setHighlightedAt(null);
      setShowFlagDismiss(false);
      setFlagDismissReason('');
      setMsg({ ok: true, text: 'Flag dismissed.' });
      setTimeout(() => setMsg(null), 3500);
      if (window.location.hash.startsWith('#/commentReview/')) {
        window.history.replaceState(null, '', '#/commentReview');
      }
    } catch (e) {
      setMsg({ ok: false, text: (e as Error).message });
    } finally {
      setFlagResolveBusy(false);
    }
  }

  // Round 6: helper used in JSX to decide whether to flash the ring
  // around a given textarea. Returns the wrapper props (ref + class).
  function highlightProps(kind: 'subjectComment' | 'classTeacherComment' | 'hodComment', subjectId?: string) {
    if (!flagContext || !highlightedAt) return {};
    const match =
      flagContext.target === kind &&
      (kind !== 'subjectComment' || flagContext.subjectId === subjectId);
    if (!match) return {};
    return {
      ref: highlightTargetRef,
      'data-flag-highlight': 'true',
    };
  }

  const load = useCallback(async () => {
    if (!classId) return;
    setLoading(true);
    setMsg(null);
    try {
      const assembled = await assembleClassReport({
        year,
        term,
        classId,
        classes,
        students,
        subjects,
        teachers,
      });
      setRows(assembled);

      const marksSubj: Record<string, SubjRow[]> = {};
      const md: Record<string, Record<string, { ca?: CAMonths; exam?: MaybeMark; comment?: string; teacherId?: string }>> = {};
      for (const subj of subjects) {
        const mId = `${year}-${term}-${classId}-${subj.id}`;
        const mSnap = await getDoc(doc(db, COLLECTIONS.marks, mId));
        if (!mSnap.exists()) continue;
        const entries =
          (
            mSnap.data() as {
              entries?: Record<string, { ca?: CAMonths; exam?: MaybeMark; comment?: string; teacherId?: string }>;
            }
          ).entries ?? {};
        for (const s of roster) {
          const e = entries[s.id];
          if (!e) continue;
          // Who entered this subject comment: the stamped teacherId, else the
          // student's current subject teacher. Show "Title Surname".
          const ownerId = e.teacherId || s.subjectTeachers?.[subj.id];
          const t = teachers.find((x) => x.id === ownerId);
          (marksSubj[s.id] ??= []).push({
            subjectId: subj.id,
            name: subjName(subj.id),
            teacherName: t ? `${t.title ?? ''} ${t.lastName ?? ''}`.trim() : '',
            original: e.comment ?? '',
          });
          (md[s.id] ??= {})[subj.id] = { ca: e.ca, exam: e.exam, comment: e.comment, teacherId: e.teacherId };
        }
      }
      Object.values(marksSubj).forEach((a) =>
        a.sort((x, y) => x.name.localeCompare(y.name)),
      );
      setMarkData(md);
      setSubjRows(marksSubj);

      const cId = `${year}-${term}-${classId}`;
      const cSnap = await getDoc(
        doc(db, COLLECTIONS.reportComments, cId),
      );
      const entries =
        (cSnap.exists()
          ? (cSnap.data() as ReportCommentsDocument).entries
          : {}) ?? {};
      setExisting(entries);

      const e: typeof edits = {};
      roster.forEach((s) => {
        const ce = entries[s.id];
        const subjEdit: Record<string, string> = {};
        (marksSubj[s.id] ?? []).forEach((sr) => {
          // option 2: the marks comment IS the record; show it
          subjEdit[sr.subjectId] = sr.original;
        });
        e[s.id] = {
          subjects: subjEdit,
          classTeacher: ce?.classTeacherComment ?? '',
          hoy: ce?.hodComment ?? '',
        };
      });
      setEdits(e);
      setStudentIds(roster.map((s) => s.id));
      setIdx(0);
      setDirty(false);
    } catch (err) {
      setMsg({ ok: false, text: (err as Error).message });
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [classId, year, term, roster.length]);

  // Deep-link from a flag notification: read the one-shot marker
  // Shell set, select that class, remember the student to jump to.
  useEffect(() => {
    try {
      const raw = sessionStorage.getItem('lkc.flagTarget');
      if (raw) {
        sessionStorage.removeItem('lkc.flagTarget');
        const ctx = JSON.parse(raw) as {
          classId?: string;
          studentId?: string;
        };
        if (ctx.classId) setClassId(ctx.classId);
        if (ctx.studentId) setPendingStudent(ctx.studentId);
      }
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    if (classId) load();
  }, [classId, year, term, load]);

  // Once the roster has loaded, jump to the deep-linked student.
  useEffect(() => {
    if (pendingStudent && studentIds.length) {
      const i = studentIds.indexOf(pendingStudent);
      if (i >= 0) setIdx(i);
      setPendingStudent(null);
    }
  }, [pendingStudent, studentIds]);

  const sid = studentIds[idx];
  const student = roster.find((s) => s.id === sid);
  const row = rows.find((r) => r.name === student?.fullName);
  const E = sid ? edits[sid] : undefined;

  // Internal audit caption for the HOY comment: "Form N HOY (Teacher Name)".
  // "Form N" is the class being reviewed (single-class context), so it always
  // renders. The NAME resolves authorId -> user account -> getLinkedTeacher, and
  // only admin/head have the `users` directory in scope (Firestore rules); for
  // editors (`users` empty) and every pre-migration comment whose authorId is the
  // old hatted teacher-account UID, the account/teacher simply isn't found and we
  // fall back to a clean "Form N HOY" with no parenthetical — never broken output.
  const reviewClass = classes.find((c) => c.id === classId);
  const hoyFormLabel = reviewClass?.form ? `Form ${reviewClass.form} HOY` : 'HOY';
  const hoyAuthorName = (() => {
    const authorId = sid ? existing[sid]?.hodCommentMeta?.authorId : undefined;
    if (!authorId) return null;
    const account = users.find((u) => u.uid === authorId || u.id === authorId);
    if (!account) return null;
    return getLinkedTeacher(account, teachers)?.displayName ?? null;
  })();
  const hoyAuthorCaption = hoyAuthorName
    ? `${hoyFormLabel} (${hoyAuthorName})`
    : hoyFormLabel;

  function setSubjectEdit(subjectId: string, v: string) {
    if (!sid) return;
    setEdits((p) => ({
      ...p,
      [sid]: {
        ...p[sid],
        subjects: { ...p[sid].subjects, [subjectId]: v },
      },
    }));
    setDirty(true); touchAutoSave();
  }
  function setCT(v: string) {
    if (!sid) return;
    setEdits((p) => ({
      ...p,
      [sid]: { ...p[sid], classTeacher: v },
    }));
    setDirty(true); touchAutoSave();
  }
  function setHOY(v: string) {
    if (!sid) return;
    setEdits((p) => ({ ...p, [sid]: { ...p[sid], hoy: v } }));
    setDirty(true); touchAutoSave();
  }

  function tidyAll() {
    if (!sid || !E) return;
    setEdits((p) => {
      const cur = p[sid];
      const s: Record<string, string> = {};
      Object.entries(cur.subjects).forEach(
        ([k, v]) => (s[k] = tidyText(v)),
      );
      return {
        ...p,
        [sid]: {
          subjects: s,
          classTeacher: tidyText(cur.classTeacher),
          hoy: tidyText(cur.hoy),
        },
      };
    });
    setDirty(true); touchAutoSave();
    setMsg({
      ok: true,
      text: 'Tidied (cleanup only — please proofread).',
    });
    setTimeout(() => setMsg(null), 3000);
  }

  function previewReplaceCount(): number {
    if (!findText) return 0;
    let c = 0;
    const ids = classScope ? studentIds : sid ? [sid] : [];
    for (const id of ids) {
      const cur = edits[id];
      if (!cur) continue;
      [
        cur.classTeacher,
        cur.hoy,
        ...Object.values(cur.subjects),
      ].forEach((t) => (c += t.split(findText).length - 1));
    }
    return c;
  }

  function runReplace() {
    if (!findText) return;
    const f = findText;
    const r = replText;
    const apply = (txt: string) =>
      txt.includes(f) ? txt.split(f).join(r) : txt;
    setEdits((prev) => {
      const next = { ...prev };
      const ids = classScope ? studentIds : sid ? [sid] : [];
      for (const id of ids) {
        const cur = next[id];
        if (!cur) continue;
        const s: Record<string, string> = {};
        Object.entries(cur.subjects).forEach(
          ([k, v]) => (s[k] = apply(v)),
        );
        next[id] = {
          subjects: s,
          classTeacher: apply(cur.classTeacher),
          hoy: apply(cur.hoy),
        };
      }
      return next;
    });
    setDirty(true); touchAutoSave();
    const n = confirmBulk?.count ?? 0;
    setConfirmBulk(null);
    setMsg({
      ok: true,
      text: `Replaced ${n} occurrence${n === 1 ? '' : 's'}${
        classScope ? ' across the class' : ''
      }. Not saved until you click Save all.`,
    });
    setTimeout(() => setMsg(null), 4000);
  }

  // Round 12: auto-save after 30 s of inactivity OR on tab-hide.
  const { touch: touchAutoSave } = useAutoSave(
    async () => { if (dirty && classId && editable) await save(); },
    300000,
    dirty && !!classId && editable,
  );

  // 13.6 — Editor's proof PDF. For every student in the class lists EVERY
  // subject they take (even with no mark/comment yet), the term CA + exam
  // mark, the comment author, and the comment — plus the class-teacher and
  // Head of Year comments. Reads the in-workspace values (incl. unsaved edits).
  const [proofBusy, setProofBusy] = useState(false);
  async function exportCommentProof() {
    if (!classId || roster.length === 0) return;
    setProofBusy(true);
    try {
      const clsName = classes.find((c) => c.id === classId)?.name ?? classId;
      const fmtMark = (m: MaybeMark | null | undefined): string => {
        if (m === null || m === undefined || m === '') return '';
        if (isAbsent(m)) return 'ABS';
        return String(m);
      };
      const proofStudents: CommentProofStudent[] = roster.map((s) => {
        const md = markData[s.id] ?? {};
        const ed = edits[s.id];
        // Order subjects to MATCH the on-screen Comment Review fields for easy
        // side-by-side editing: the marked subjects exactly as subjRows lists
        // them (the review order), then any remaining subjects the student
        // takes but has no marks/comment for, appended alphabetically.
        const allSids = s.subjects ?? [];
        const marked = (subjRows[s.id] ?? [])
          .map((sr) => sr.subjectId)
          .filter((sid) => allSids.includes(sid));
        const unmarked = allSids
          .filter((sid) => !marked.includes(sid))
          .sort((a, b) => subjName(a).localeCompare(subjName(b)));
        const orderedSids = [...marked, ...unmarked];
        const subjectRows = orderedSids
          .map((sid) => {
            const subj = subjects.find((x) => x.id === sid);
            if (!subj) return null;
            const entry = md[sid];
            const d = deriveMark(term, entry?.ca ?? {}, entry?.exam, schemeForSubject(subj));
            const ownerId = entry?.teacherId || s.subjectTeachers?.[sid];
            const t = teachers.find((x) => x.id === ownerId);
            return {
              subject: subj.name,
              ca: d.caMark === null ? '' : String(d.caMark),
              exam: fmtMark(d.exam),
              teacher: t ? `${t.title ?? ''} ${t.lastName ?? ''}`.trim() : '',
              comment: (ed?.subjects?.[sid] ?? entry?.comment ?? '').trim(),
            };
          })
          .filter((r): r is NonNullable<typeof r> => r !== null);
        return {
          name: s.fullName,
          subjects: subjectRows,
          classTeacher: (ed?.classTeacher ?? '').trim(),
          headOfYear: (ed?.hoy ?? '').trim(),
        };
      });
      const logo = await loadLogoDataUrl();
      buildCommentProofPdf({
        className: clsName,
        metaLine: `${clsName} · ${termLabel(term)} ${year}`,
        students: proofStudents,
        logoDataUrl: logo,
        generatedBy: userData?.displayName,
      }).save(`CommentProof_${clsName}_${term}_${year}`.replace(/\s+/g, '_') + '.pdf');
    } catch (e) {
      setMsg({ ok: false, text: (e as Error).message });
    } finally {
      setProofBusy(false);
    }
  }

  async function save() {
    // VERSION GATE (hard block): a stale client must never write to the shared
    // reportMarks doc - the pre-fix bundle rewrites the whole entries map and
    // clobbers other teachers' marks. Abort before any setDoc. Fail-open on
    // unknown serverBuild - see isClientStale.
    if (isClientStale(serverBuild)) {
      setStaleBlocked(true);
      return;
    }
    if (!classId) return;
    setSaving(true);
    setSaveModal({ show: true, status: 'saving', message: 'Saving corrections…' });
    try {
      const cId = `${year}-${term}-${classId}`;
      const meta = (): CommentMeta => {
        // Round 12 hotfix: same undefined-rejection bug as ReviewWorkspace.
        // Build the object then strip undefineds before returning — Firestore
        // rejects `undefined` anywhere in the payload.
        const m: Record<string, unknown> = {
          authorId: userData?.uid,
          updatedAt: new Date().toISOString(),
          editedByEditor: true,
          editorId: userData?.uid,
          editedAt: new Date().toISOString(),
        };
        if (userRole) m.authorRole = userRole;
        if (userData?.displayName) m.editorName = userData.displayName;
        return stripUndefined(m) as unknown as CommentMeta;
      };

      // 1. HOY + class-teacher comments -> reportComments (as before)
      const entries: Record<string, StudentReportComments> = {};
      roster.forEach((s) => {
        const prev = existing[s.id] ?? {
          classTeacherComment: '',
          hodComment: '',
        };
        const ed = edits[s.id];
        if (!ed) {
          entries[s.id] = prev;
          return;
        }
        entries[s.id] = {
          ...prev,
          classTeacherComment: sanitizeComment(ed.classTeacher, 800),
          hodComment: sanitizeComment(ed.hoy, 800),
          classTeacherCommentMeta:
            ed.classTeacher !== (prev.classTeacherComment ?? '')
              ? meta()
              : prev.classTeacherCommentMeta,
          hodCommentMeta:
            ed.hoy !== (prev.hodComment ?? '')
              ? meta()
              : prev.hodCommentMeta,
        };
      });
      // WIPE GUARD (13.7): never write an empty entries map — a merge with {}
      // can replace the field and erase other reviewers' comments on the doc.
      if (Object.keys(entries).length > 0) {
        await setDoc(
          doc(db, COLLECTIONS.reportComments, cId),
          {
            year,
            term,
            classId,
            entries,
            updatedAt: new Date().toISOString(),
          },
          { merge: true },
        );
      }

      // 2. Subject comments -> written DIRECTLY into the marks
      //    record (option 2: the editor's text is the final
      //    record). Per subject doc, re-read fresh and update ONLY
      //    the comment field of changed students' entries — never
      //    touch ca / exam / termMark.
      const origById: Record<string, Record<string, string>> = {};
      roster.forEach((s) => {
        origById[s.id] = {};
        (subjRows[s.id] ?? []).forEach(
          (sr) => (origById[s.id][sr.subjectId] = sr.original),
        );
      });

      for (const subj of subjects) {
        // does any student have a changed comment for this subject?
        const changed = roster.filter((s) => {
          const ed = edits[s.id];
          if (!ed || !(subj.id in ed.subjects)) return false;
          return (
            sanitizeComment(ed.subjects[subj.id], 600) !==
            (origById[s.id]?.[subj.id] ?? '')
          );
        });
        if (changed.length === 0) continue;

        const mId = `${year}-${term}-${classId}-${subj.id}`;
        const mRef = doc(db, COLLECTIONS.marks, mId);
        const mSnap = await getDoc(mRef);
        if (!mSnap.exists()) continue; // no marks doc -> nothing to edit
        const data = mSnap.data() as {
          entries?: Record<
            string,
            Record<string, unknown> & { comment?: string }
          >;
        };
        const mEntries = { ...(data.entries ?? {}) };
        changed.forEach((s) => {
          const prevEntry = mEntries[s.id] ?? {};
          mEntries[s.id] = {
            ...prevEntry, // preserve ca / exam / termMark etc.
            comment: sanitizeComment(
              edits[s.id].subjects[subj.id],
              600,
            ),
            commentEditedBy: userData?.uid,
            commentEditedAt: new Date().toISOString(),
          };
        });
        await setDoc(
          mRef,
          { entries: mEntries, updatedAt: new Date().toISOString() },
          { merge: true },
        );
      }

      // refresh local originals so the "edited" markers reset
      setSubjRows((prev) => {
        const next: Record<string, SubjRow[]> = {};
        for (const s of roster) {
          next[s.id] = (prev[s.id] ?? []).map((sr) => ({
            ...sr,
            original: sanitizeComment(
              edits[s.id]?.subjects?.[sr.subjectId] ?? sr.original,
              600,
            ),
          }));
        }
        return next;
      });
      setExisting((p) => ({ ...p, ...entries }));
      setDirty(false);
      setSaveModal({ show: true, status: 'success', message: 'Corrections saved.' });

      // Audit: log every comment change with before/after
      const editDetails: Array<{ studentId: string; studentName: string; field: string; before: string; after: string }> = [];
      roster.forEach((s) => {
        const ed = edits[s.id];
        const prev = existing[s.id];
        if (!ed || !prev) return;
        if (ed.classTeacher !== (prev.classTeacherComment ?? '')) {
          editDetails.push({ studentId: s.id, studentName: s.fullName, field: 'classTeacherComment', before: prev.classTeacherComment ?? '', after: ed.classTeacher });
        }
        if (ed.hoy !== (prev.hodComment ?? '')) {
          editDetails.push({ studentId: s.id, studentName: s.fullName, field: 'hodComment', before: prev.hodComment ?? '', after: ed.hoy });
        }
        for (const [subId, newComment] of Object.entries(ed.subjects)) {
          const origComment = origById[s.id]?.[subId] ?? '';
          if (sanitizeComment(newComment, 600) !== origComment) {
            editDetails.push({ studentId: s.id, studentName: s.fullName, field: `subjectComment:${subId}`, before: origComment, after: newComment });
          }
        }
      });
      if (editDetails.length > 0) {
        auditFromContext(userData, 'comments_edited_by_editor', 'academic', {
          targetId: classId,
          targetType: 'comment',
          targetName: classes.find((c) => c.id === classId)?.name ?? classId,
          details: {
            year, term, classId,
            editCount: editDetails.length,
            edits: editDetails,
          },
        });
      }
    } catch (err) {
      setSaveModal({ show: true, status: 'error', message: (err as Error).message });
    } finally {
      setSaving(false);
    }
  }

  if (!permissions.canViewCommentReview) {
    return (
      <div className="rounded-2xl border border-amber-200 bg-amber-50 text-amber-700 px-4 py-3 text-sm dark:bg-amber-900/30 dark:border-amber-700 dark:text-amber-300">
        This review panel is for editors only.
      </div>
    );
  }

  const go = (d: number) =>
    setIdx((i) =>
      Math.max(0, Math.min(studentIds.length - 1, i + d)),
    );

  return (
    <div className="space-y-4">
      <GradientHero
        icon={ClipboardCheck}
        title="Comment Review"
        subtitle={
          isEditorRole
            ? 'Editor — correct every comment for a student in one place'
            : "Read-only — print the editor's proof of your class's comments"
        }
        actions={
          isEditorRole && classId && activeTab === 'review' ? (
            <>
              {dirty ? (
                <GlassPill tone="amber">
                  <span className="w-2 h-2 bg-amber-300 rounded-full animate-pulse" />
                  Unsaved
                </GlassPill>
              ) : (
                <GlassPill tone="green">Saved</GlassPill>
              )}
              <button
                onClick={save}
                disabled={!dirty || !editable || saving}
                className={`flex items-center gap-2 px-6 py-3 font-semibold rounded-xl transition-all duration-300 shadow-lg ${
                  !dirty || !editable
                    ? 'bg-white/20 text-white/60 cursor-not-allowed'
                    : 'bg-white text-[#1b3a6b] hover:bg-blue-50 hover:scale-105'
                }`}
              >
                <SaveIcon size={18} />
                {saving ? 'Saving…' : 'Save all'}
              </button>
            </>
          ) : null
        }
      />

      {/* Round 6: flag-context banner (only when arrived via deep-link). */}
      {flagContext && (
        <div className="rounded-2xl border-2 border-amber-300 bg-amber-50/80 dark:bg-amber-900/20 dark:border-amber-700 p-4 shadow-md">
          <div className="flex items-start gap-3">
            <div className="shrink-0 w-10 h-10 rounded-xl bg-amber-100 dark:bg-amber-800/40 flex items-center justify-center">
              <Flag size={18} className="text-amber-700 dark:text-amber-300" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-[10px] uppercase tracking-wide font-bold text-amber-700 dark:text-amber-300">
                  Resolving a flag
                </span>
                <span className="text-[10px] text-amber-700/70 dark:text-amber-300/70">
                  raised by {flagContext.flaggedByName ?? 'an editor'}
                </span>
              </div>
              <p className="text-sm font-semibold text-amber-900 dark:text-amber-100 mt-0.5">
                {flagContext.studentName} ·{' '}
                {flagContext.target === 'subjectComment' && flagContext.subjectName
                  ? `${flagContext.subjectName} comment`
                  : flagContext.target === 'classTeacherComment'
                    ? 'Class teacher comment'
                    : flagContext.target === 'hodComment'
                      ? 'HOY comment'
                      : flagContext.target === 'mark' && flagContext.subjectName
                        ? `${flagContext.subjectName} mark`
                        : 'Comment'}
              </p>
              <p className="text-sm text-amber-800 dark:text-amber-200 mt-1">
                <span className="font-medium">Reason:</span>{' '}
                {flagContext.reasonLabel || flagContext.reason}
                {flagContext.notes ? (
                  <span className="text-amber-700/80 dark:text-amber-300/80">
                    {' '}
                    — {flagContext.notes}
                  </span>
                ) : null}
              </p>
              <p className="text-[11px] text-amber-700/70 dark:text-amber-300/70 mt-1.5">
                Verify the comment below has been corrected, then mark
                the flag resolved. Dismiss only if the flag was raised
                in error.
              </p>
            </div>
            <div className="flex flex-col gap-1.5 shrink-0">
              <button
                onClick={resolveFlagInContext}
                disabled={flagResolveBusy}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold shadow-sm disabled:opacity-50"
              >
                <Check size={13} /> Resolve flag
              </button>
              <button
                onClick={() => setShowFlagDismiss(true)}
                disabled={flagResolveBusy}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-amber-300 dark:border-amber-700 text-amber-800 dark:text-amber-200 hover:bg-amber-100 dark:hover:bg-amber-900/40 text-xs font-semibold disabled:opacity-50"
              >
                <XIcon size={13} /> Dismiss
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Round 6: in-context Dismiss modal */}
      {showFlagDismiss && flagContext && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
          onClick={() => {
            setShowFlagDismiss(false);
            setFlagDismissReason('');
          }}
        >
          <div
            className="w-full max-w-md p-6 bg-white/95 dark:bg-gray-900/85 backdrop-blur-xl border border-white/50 dark:border-white/10 ring-1 ring-black/5 rounded-2xl shadow-2xl"
            onClick={(e: { stopPropagation: () => void }) => e.stopPropagation()}
          >
            <h2 className="text-lg font-bold text-gray-900 dark:text-white mb-2 flex items-center gap-2">
              <AlertTriangle size={18} className="text-amber-500" />
              Dismiss without resolving?
            </h2>
            <p className="text-sm text-gray-600 dark:text-gray-300 mb-3">
              Use this when the flag was raised in error or is a
              duplicate. If the comment needed correcting, click
              <strong> Resolve flag </strong> instead after verifying
              the fix.
            </p>
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
              Why are you dismissing this?
            </label>
            <input
              type="text"
              value={flagDismissReason}
              onChange={(e) => setFlagDismissReason(e.target.value)}
              placeholder="e.g. raised in error, duplicate of another flag…"
              className="w-full px-3 py-2 rounded-lg border-2 border-gray-200 dark:border-gray-700 dark:bg-gray-800 focus:border-primary focus:outline-none text-sm mb-4"
              autoFocus
            />
            <div className="flex justify-end gap-2">
              <button
                onClick={() => {
                  setShowFlagDismiss(false);
                  setFlagDismissReason('');
                }}
                className="px-4 py-2 text-sm font-semibold text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg"
              >
                Cancel
              </button>
              <button
                onClick={dismissFlagInContext}
                disabled={!flagDismissReason.trim() || flagResolveBusy}
                className="px-4 py-2 text-sm font-semibold bg-amber-600 hover:bg-amber-700 text-white rounded-lg disabled:opacity-50"
              >
                Dismiss flag
              </button>
            </div>
          </div>
        </div>
      )}

      {!editable && classId && (
        <div className="flex items-center gap-2 text-sm rounded-xl px-4 py-3 border bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-900/30 dark:border-amber-700 dark:text-amber-300">
          <AlertTriangle size={15} /> {termLabel(term)} {year} is{' '}
          {termState}. Editing is restricted.
        </div>
      )}
      {msg && (
        <div
          className={`text-sm rounded-xl px-4 py-3 border flex items-center gap-2 ${
            msg.ok
              ? 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-900/30 dark:border-emerald-700 dark:text-emerald-300'
              : 'bg-red-50 text-red-700 border-red-200 dark:bg-red-900/30 dark:border-red-700 dark:text-red-300'
          }`}
        >
          {msg.ok ? (
            <Check size={15} />
          ) : (
            <AlertTriangle size={15} />
          )}
          {msg.text}
        </div>
      )}

      <SoftCard pad="p-5">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
          <div>
            <label className="text-sm font-semibold text-gray-700 dark:text-gray-200">
              Class
            </label>
            <select
              value={classId}
              onChange={(e) => setClassId(e.target.value)}
              className={glassFieldClass('mt-1.5 sm:w-80')}
            >
              <option value="">— select class —</option>
              {[...classes]
                .sort((a, b) => a.name.localeCompare(b.name))
                .map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
            </select>
          </div>
          {classId && roster.length > 0 && (
            <button
              type="button"
              onClick={exportCommentProof}
              disabled={proofBusy}
              title="Print an editor's proof: every student's subjects, marks and comments"
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold bg-primary/10 text-primary hover:bg-primary/20 disabled:opacity-50 transition-all"
            >
              <FileText size={16} />
              {proofBusy ? 'Preparing…' : "Editor's proof PDF"}
            </button>
          )}
        </div>
      </SoftCard>

      {/* Read-only viewers (class teachers) only get the class picker + the
          Editor's proof button above; the editing workspace is editors-only. */}
      {!isEditorRole && classId && (
        <SoftCard pad="p-4">
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Use <span className="font-semibold text-primary">Editor's proof PDF</span> above to
            print every student's subjects, marks and comments for this class. Editing comments is
            done by the editor team.
          </p>
        </SoftCard>
      )}

      {/* Round 7: tab strip — Review (editor surface) vs History
          (read-only edit log). Only meaningful once a class is
          selected, so we render it gated on classId. */}
      {isEditorRole && classId && (
        <SoftCard pad="p-1.5">
          <div className="flex gap-1">
            <button
              type="button"
              onClick={() => setActiveTab('review')}
              className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-sm font-semibold transition-colors ${
                activeTab === 'review'
                  ? 'bg-primary text-white shadow-sm'
                  : 'text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-800'
              }`}
            >
              <ClipboardCheck size={15} />
              Review
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('history')}
              className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-sm font-semibold transition-colors ${
                activeTab === 'history'
                  ? 'bg-primary text-white shadow-sm'
                  : 'text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-800'
              }`}
            >
              <History size={15} />
              Edit history
            </button>
          </div>
        </SoftCard>
      )}

      {/* Round 7: edit-history tab content */}
      {isEditorRole && classId && activeTab === 'history' && (
        <EditHistory
          year={year}
          term={term}
          classId={classId}
          className={
            classes.find((c) => c.id === classId)?.name ?? classId
          }
          subjName={subjName}
          roster={roster.map((s) => ({ id: s.id, fullName: s.fullName }))}
          onOpenStudent={(sid) => {
            const i = roster.findIndex((s) => s.id === sid);
            if (i >= 0) setIdx(i);
            setActiveTab('review');
          }}
        />
      )}

      {isEditorRole && classId && activeTab === 'review' && loading && (
        <Spinner label="Loading class…" />
      )}

      {isEditorRole && classId && activeTab === 'review' && !loading && student && E && (
        <>
          <SoftCard pad="p-4">
            <div className="flex flex-wrap items-end gap-3">
              <button
                type="button"
                onClick={tidyAll}
                disabled={!editable}
                className="flex items-center gap-2 px-4 py-2.5 text-sm font-semibold rounded-xl bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-200 disabled:opacity-50"
                title="Cleanup pass — not a grammar checker"
              >
                <Wand2 size={15} />
                Tidy this student's comments
              </button>
              <div className="flex items-end gap-2 flex-1 min-w-[280px]">
                <div className="flex-1">
                  <label className="text-[11px] font-semibold text-gray-500 uppercase">
                    Find
                  </label>
                  <input
                    value={findText}
                    onChange={(e) => setFindText(e.target.value)}
                    placeholder="e.g. wrong name"
                    className={glassFieldClass('py-2 text-sm')}
                  />
                </div>
                <div className="flex-1">
                  <label className="text-[11px] font-semibold text-gray-500 uppercase">
                    Replace with
                  </label>
                  <input
                    value={replText}
                    onChange={(e) => setReplText(e.target.value)}
                    placeholder="correct text"
                    className={glassFieldClass('py-2 text-sm')}
                  />
                </div>
                <button
                  type="button"
                  disabled={!editable || !findText}
                  onClick={() =>
                    setConfirmBulk({
                      count: previewReplaceCount(),
                    })
                  }
                  className="flex items-center gap-2 px-4 py-2 text-sm font-semibold rounded-xl bg-gradient-to-r from-[#2d5295] to-[#1b3a6b] text-white shadow-md disabled:opacity-40"
                >
                  <Replace size={15} />
                  Fix
                </button>
              </div>
              <label className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-300">
                <input
                  type="checkbox"
                  checked={classScope}
                  onChange={(e) =>
                    setClassScope(e.target.checked)
                  }
                />
                Apply across the whole class
              </label>
            </div>
            {confirmBulk && (
              <div className="mt-3 rounded-xl border border-amber-300 bg-amber-50 dark:bg-amber-900/30 dark:border-amber-700 p-3 text-sm">
                <p className="text-amber-800 dark:text-amber-200 flex items-start gap-2">
                  <AlertTriangle
                    size={15}
                    className="mt-0.5 shrink-0"
                  />
                  <span>
                    Replace “{findText}” with “{replText}” —{' '}
                    <strong>{confirmBulk.count}</strong> occurrence
                    {confirmBulk.count === 1 ? '' : 's'}
                    {classScope
                      ? ' across every student in this class'
                      : " in this student's comments"}
                    . Not saved until you click Save all.
                  </span>
                </p>
                <div className="flex gap-2 mt-2">
                  <button
                    onClick={runReplace}
                    className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-amber-600 text-white"
                  >
                    Apply
                  </button>
                  <button
                    onClick={() => setConfirmBulk(null)}
                    className="px-3 py-1.5 text-xs rounded-lg border border-gray-300 dark:border-gray-600"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </SoftCard>

          {/* Student header */}
          <SoftCard pad="p-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-primary/10 text-primary flex items-center justify-center font-bold text-lg shrink-0">
                {student.fullName
                  .split(' ')
                  .map((p) => p[0])
                  .slice(0, 2)
                  .join('')
                  .toUpperCase()}
              </div>
              <div className="min-w-0">
                <p className="text-lg font-bold text-gray-900 dark:text-white truncate">
                  {student.fullName}
                </p>
                <p className="text-xs text-gray-500">
                  {row?.className} · {row?.subjects.length ?? 0} subjects · Student {idx + 1} of{' '}
                  {studentIds.length}
                </p>
              </div>
            </div>
          </SoftCard>

          {/* Comments — one focused column, the mark shown beside each field */}
          <SoftCard pad="p-4 sm:p-5">
              <div className="space-y-4 max-h-[560px] overflow-y-auto pr-1">
                {(subjRows[sid] ?? []).map((sr) => {
                  const hp = highlightProps('subjectComment', sr.subjectId);
                  const flash = 'data-flag-highlight' in hp;
                  return (
                    <div
                      key={sr.subjectId}
                      ref={flash ? highlightTargetRef : undefined}
                      className={
                        flash
                          ? 'rounded-xl ring-4 ring-amber-400/70 animate-pulse p-2 -m-2'
                          : ''
                      }
                    >
                      <div className="flex items-center justify-between gap-2">
                        <label className="text-xs font-bold uppercase tracking-wide text-gray-500">
                          {sr.name}{' '}
                          {sr.teacherName && (
                            <span className="text-[#2d5295] dark:text-blue-300 normal-case font-semibold">
                              · {sr.teacherName}
                            </span>
                          )}
                        </label>
                        <div className="flex items-center gap-2 shrink-0">
                        {(() => {
                          const mk = (row?.subjects ?? []).find((x) => x.name === sr.name);
                          if (!mk) return null;
                          const gc = gradeColor(mk.grade);
                          const m = mk.exam === 'ABS' ? 'ABS' : (mk.exam ?? mk.termMark);
                          return (
                            <span className="flex items-center gap-1.5 shrink-0">
                              <span className="text-sm font-bold text-gray-700 dark:text-gray-200">
                                {m ?? '–'}
                                {typeof m === 'number' && '%'}
                              </span>
                              <span
                                className="text-xs font-bold text-white px-1.5 py-0.5 rounded"
                                style={{ backgroundColor: rgb(gc) }}
                              >
                                {mk.grade || '-'}
                              </span>
                            </span>
                          );
                        })()}
                        {isEditorRole && (
                          <button
                            type="button"
                            title="Flag this comment — notifies the teacher"
                            onClick={() => setFlagModal({ target: 'subjectComment', subjectId: sr.subjectId, subjectName: sr.name, teacherName: sr.teacherName, commentPreview: E.subjects[sr.subjectId] ?? sr.original })}
                            className="inline-flex items-center gap-1 text-[11px] font-semibold text-amber-700 dark:text-amber-300 hover:bg-amber-50 dark:hover:bg-amber-900/30 px-2 py-1 rounded-lg border border-amber-200 dark:border-amber-800"
                          >
                            <Flag size={12} /> Flag
                          </button>
                        )}
                        </div>
                      </div>
                      <textarea
                        value={E.subjects[sr.subjectId] ?? ''}
                        disabled={!editable}
                        onChange={(e) =>
                          setSubjectEdit(
                            sr.subjectId,
                            e.target.value,
                          )
                        }
                        rows={3}
                        className="w-full mt-1 px-3 py-2 rounded-xl bg-white border-2 border-gray-200 text-sm focus:border-[#2d5295] focus:ring-4 focus:ring-[#1b3a6b]/10 focus:outline-none dark:bg-gray-900 dark:border-gray-700 dark:text-white disabled:opacity-50"
                      />
                      {E.subjects[sr.subjectId] !== sr.original && (
                        <p className="text-[11px] text-amber-600 mt-0.5">
                          edited — saved as the final comment on save
                        </p>
                      )}
                    </div>
                  );
                })}

                {(() => {
                  const ctHp = highlightProps('classTeacherComment');
                  const ctFlash = 'data-flag-highlight' in ctHp;
                  return (
                    <div
                      ref={ctFlash ? highlightTargetRef : undefined}
                      className={`rounded-xl border-2 border-[#2d5295]/30 bg-gradient-to-br from-[#1b3a6b]/[0.04] to-[#d4a92c]/[0.04] p-3 ${
                        ctFlash ? 'ring-4 ring-amber-400/70 animate-pulse' : ''
                      }`}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <label className="text-[11px] font-bold uppercase tracking-wide text-[#1b3a6b] dark:text-blue-300">
                          Class teacher comment
                        </label>
                        {isEditorRole && (
                          <button
                            type="button"
                            title="Flag this comment — notifies the class teacher"
                            onClick={() => setFlagModal({ target: 'classTeacherComment', commentPreview: E.classTeacher })}
                            className="inline-flex items-center gap-1 text-[11px] font-semibold text-amber-700 dark:text-amber-300 hover:bg-amber-50 dark:hover:bg-amber-900/30 px-2 py-1 rounded-lg border border-amber-200 dark:border-amber-800"
                          >
                            <Flag size={12} /> Flag
                          </button>
                        )}
                      </div>
                      <textarea
                        value={E.classTeacher}
                        disabled={!editable}
                        onChange={(e) => setCT(e.target.value)}
                        rows={3}
                        className="w-full mt-1 px-3 py-2 rounded-xl bg-white border-2 border-[#2d5295]/40 text-sm shadow-inner focus:border-[#2d5295] focus:ring-4 focus:ring-[#1b3a6b]/15 focus:outline-none dark:bg-gray-900 dark:text-white disabled:opacity-50"
                      />
                    </div>
                  );
                })()}
                {(() => {
                  const hoyHp = highlightProps('hodComment');
                  const hoyFlash = 'data-flag-highlight' in hoyHp;
                  return (
                    <div
                      ref={hoyFlash ? highlightTargetRef : undefined}
                      className={`rounded-xl border-2 border-[#2d5295]/30 bg-gradient-to-br from-[#1b3a6b]/[0.04] to-[#d4a92c]/[0.04] p-3 ${
                        hoyFlash ? 'ring-4 ring-amber-400/70 animate-pulse' : ''
                      }`}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <label className="text-[11px] font-bold uppercase tracking-wide text-[#1b3a6b] dark:text-blue-300">
                          Head of Year comment
                        </label>
                        {isEditorRole && (
                          <button
                            type="button"
                            title="Flag this comment — notifies the HOY"
                            onClick={() => setFlagModal({ target: 'hodComment', commentPreview: E.hoy })}
                            className="inline-flex items-center gap-1 text-[11px] font-semibold text-amber-700 dark:text-amber-300 hover:bg-amber-50 dark:hover:bg-amber-900/30 px-2 py-1 rounded-lg border border-amber-200 dark:border-amber-800"
                          >
                            <Flag size={12} /> Flag
                          </button>
                        )}
                      </div>
                      {existing[sid]?.hodComment?.trim() ? (
                        <p className="text-[11px] text-gray-400 mt-0.5 not-italic">
                          {hoyAuthorCaption}
                        </p>
                      ) : null}
                      <textarea
                        value={E.hoy}
                        disabled={!editable}
                        onChange={(e) => setHOY(e.target.value)}
                        rows={3}
                        className="w-full mt-1 px-3 py-2 rounded-xl bg-white border-2 border-[#2d5295]/40 text-sm shadow-inner focus:border-[#2d5295] focus:ring-4 focus:ring-[#1b3a6b]/15 focus:outline-none dark:bg-gray-900 dark:text-white disabled:opacity-50"
                      />
                    </div>
                  );
                })()}
              </div>
            </SoftCard>

          <SoftCard pad="p-3">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <button
                onClick={() => go(-1)}
                disabled={idx === 0}
                className="flex items-center gap-1 px-3 py-1.5 text-sm text-gray-600 dark:text-gray-300 disabled:opacity-40"
              >
                <ChevronLeft size={16} /> Previous
              </button>
              <select
                value={sid}
                onChange={(e) =>
                  setIdx(studentIds.indexOf(e.target.value))
                }
                className="px-3 py-1.5 rounded-lg border border-gray-300 dark:border-gray-700 dark:bg-gray-800 text-sm max-w-[240px]"
              >
                {roster.map((s, i) => (
                  <option key={s.id} value={s.id}>
                    {i + 1}. {s.fullName}
                  </option>
                ))}
              </select>
              <button
                onClick={() => go(1)}
                disabled={idx === studentIds.length - 1}
                className="flex items-center gap-1 px-3 py-1.5 text-sm text-gray-600 dark:text-gray-300 disabled:opacity-40"
              >
                Next <ChevronRight size={16} />
              </button>
            </div>
            <p className="text-center text-xs text-gray-500 mt-2">
              Student {idx + 1} of {studentIds.length}
            </p>
          </SoftCard>

          <StickySaveBar
            visible={dirty && editable}
            saving={saving}
            onSave={save}
            message="Unsaved comment edits"
            saveLabel="Save all"
          />
        </>
      )}
      <SaveConfirmation
        show={saveModal.show}
        status={saveModal.status}
        message={saveModal.message}
        onDismiss={() => setSaveModal({ ...saveModal, show: false })}
      />
      {flagModal && student && sid && classId && (
        <RaiseFlagModal
          open
          onClose={() => setFlagModal(null)}
          year={year}
          term={term}
          classId={classId}
          studentId={sid}
          studentName={student.fullName}
          target={flagModal.target}
          subjectId={flagModal.subjectId}
          subjectName={flagModal.subjectName}
          teacherName={flagModal.teacherName}
          commentPreview={flagModal.commentPreview}
        />
      )}
      {staleBlocked && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-sm w-full p-6 text-center">
            <p className="font-semibold text-gray-900 dark:text-white">App out of date</p>
            <p className="text-sm text-gray-500 mt-2">
              This app is out of date and can't save. Reload to get the latest
              version - your entries stay on screen.
            </p>
            <button
              onClick={() => window.location.reload()}
              className="mt-4 px-4 py-2 bg-[#1b3a6b] text-white text-sm font-semibold rounded-lg"
            >
              Reload
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
